/* ==========================================================================
   TYMZO AJAX CART V15
   Fichier : assets/tymzo-ajax-cart.js
   ========================================================================== */

(() => {
  'use strict';

  const cartRoot = document.querySelector('[data-tymzo-cart]');
  if (!cartRoot) return;

  const content = cartRoot.querySelector('[data-tymzo-cart-content]');
  const footer = cartRoot.querySelector('[data-tymzo-cart-footer]');
  const total = cartRoot.querySelector('[data-tymzo-cart-total]');
  const progress = cartRoot.querySelector('[data-tymzo-cart-progress]');
  const progressBar = cartRoot.querySelector('[data-tymzo-cart-progress-bar]');
  const progressText = cartRoot.querySelector('[data-tymzo-cart-progress-text]');

  const addUrl = cartRoot.dataset.cartAddUrl || '/cart/add';
  const changeUrl = cartRoot.dataset.cartChangeUrl || '/cart/change';
  const cartJsonUrl = cartRoot.dataset.cartJsonUrl || '/cart.js';

  const FREE_SHIPPING_THRESHOLD = 5900;

  let lastFocusedElement = null;
  let busy = false;

  const money = (cents) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'EUR'
      }).format(cents / 100);
    } catch {
      return `${(cents / 100).toFixed(2)} €`;
    }
  };

  const escapeHtml = (value = '') => {
    const element = document.createElement('div');
    element.textContent = value;
    return element.innerHTML;
  };

  const openCart = () => {
    lastFocusedElement = document.activeElement;
    cartRoot.classList.add('is-open');
    cartRoot.setAttribute('aria-hidden', 'false');
    document.body.classList.add('tymzo-cart-open');

    const closeButton = cartRoot.querySelector('[data-tymzo-cart-close]');
    window.setTimeout(() => closeButton?.focus(), 50);
  };

  const closeCart = () => {
    cartRoot.classList.remove('is-open');
    cartRoot.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('tymzo-cart-open');
    lastFocusedElement?.focus?.();
  };

  const renderProgress = (cart) => {
    if (!progress || !progressBar || !progressText) return;

    const remaining = Math.max(0, FREE_SHIPPING_THRESHOLD - cart.total_price);
    const percentage = Math.min(
      100,
      (cart.total_price / FREE_SHIPPING_THRESHOLD) * 100
    );

    progress.hidden = cart.item_count === 0;
    progressBar.style.width = `${percentage}%`;

    progressText.textContent =
      remaining > 0
        ? `Plus que ${money(remaining)} pour bénéficier de la livraison offerte.`
        : 'Livraison offerte débloquée.';
  };

  const itemMarkup = (item, index) => {
    const variant =
      item.variant_title && item.variant_title !== 'Default Title'
        ? `<p class="tymzo-ajax-cart__variant">${escapeHtml(item.variant_title)}</p>`
        : '';

    const image = item.image
      ? `<img src="${item.image}" alt="${escapeHtml(item.product_title)}" loading="lazy">`
      : '';

    return `
      <article class="tymzo-ajax-cart__item" data-tymzo-cart-line data-line="${index + 1}">
        <a href="${item.url}" class="tymzo-ajax-cart__image">${image}</a>

        <div class="tymzo-ajax-cart__item-content">
          <div class="tymzo-ajax-cart__item-top">
            <div>
              <h3><a href="${item.url}">${escapeHtml(item.product_title)}</a></h3>
              ${variant}
            </div>

            <button
              type="button"
              class="tymzo-ajax-cart__remove"
              data-tymzo-cart-remove
              aria-label="Retirer ${escapeHtml(item.product_title)}"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M4 7h16"></path>
                <path d="M9 7V4h6v3"></path>
                <path d="M7 7l1 13h8l1-13"></path>
              </svg>
            </button>
          </div>

          <div class="tymzo-ajax-cart__item-bottom">
            <div class="tymzo-ajax-cart__quantity">
              <button type="button" data-tymzo-cart-minus aria-label="Diminuer la quantité">−</button>
              <input
                type="number"
                min="0"
                value="${item.quantity}"
                data-tymzo-cart-quantity
                aria-label="Quantité"
              >
              <button type="button" data-tymzo-cart-plus aria-label="Augmenter la quantité">+</button>
            </div>

            <p class="tymzo-ajax-cart__line-price">${money(item.final_line_price)}</p>
          </div>
        </div>
      </article>
    `;
  };

  const emptyMarkup = () => `
    <div class="tymzo-ajax-cart__empty">
      <span aria-hidden="true">
        <svg viewBox="0 0 24 24">
          <path d="M6 8h12l-1 12H7L6 8Z"></path>
          <path d="M9 8V6a3 3 0 0 1 6 0v2"></path>
        </svg>
      </span>

      <h3>Votre panier est vide</h3>
      <p>Découvrez nos produits et composez votre routine bien-être.</p>

      <a href="/collections/all" class="tymzo-ajax-cart__continue">
        Découvrir la boutique
      </a>
    </div>
  `;
    const renderCart = (cart) => {
    if (!content || !footer || !total) return;

    content.innerHTML =
      cart.item_count > 0
        ? `<div class="tymzo-ajax-cart__items">${cart.items
            .map(itemMarkup)
            .join('')}</div>`
        : emptyMarkup();

    footer.hidden = cart.item_count === 0;
    total.textContent = money(cart.total_price);

    renderProgress(cart);

    document
      .querySelectorAll(
        '[data-cart-count], .cart-count-bubble, [data-cart-bubble]'
      )
      .forEach((element) => {
        element.textContent = cart.item_count;
        element.hidden = cart.item_count === 0;
      });

    document.dispatchEvent(
      new CustomEvent('tymzo:cart:updated', {
        detail: { cart }
      })
    );
  };

  const getCart = async () => {
    const separator = cartJsonUrl.includes('?') ? '&' : '?';

    const response = await fetch(
      `${cartJsonUrl}${separator}_=${Date.now()}`,
      {
        headers: {
          Accept: 'application/json'
        },
        cache: 'no-store'
      }
    );

    if (!response.ok) {
      throw new Error('Impossible de charger le panier.');
    }

    return response.json();
  };

  const refreshAndOpenCart = async () => {
    const cart = await getCart();
    renderCart(cart);
    openCart();
    return cart;
  };

  const changeLine = async (line, quantity) => {
    if (busy) return;
    busy = true;

    try {
      const response = await fetch(`${changeUrl}.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          line,
          quantity
        })
      });

      if (!response.ok) {
        throw new Error('Impossible de modifier le panier.');
      }

      const cart = await response.json();
      renderCart(cart);

    } catch (error) {
      console.error('[Tymzo Cart]', error);

    } finally {
      busy = false;
    }
  };

  const addForm = async (form) => {
    if (busy) return;
    busy = true;

    const button = form.querySelector('[type="submit"], [name="add"]');
    const originalText = button?.textContent;

    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.textContent = 'Ajout en cours...';
    }

    try {

      const formData = new FormData(form);

      const response = await fetch(`${addUrl}.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json'
        },
        body: formData
      });

      if (!response.ok) {

        const error = await response.json().catch(() => ({}));

        throw new Error(
          error.description || 'Impossible d’ajouter ce produit.'
        );
      }

      await refreshAndOpenCart();

    } catch (error) {

      console.error('[Tymzo Cart]', error);
      window.alert(error.message);

    } finally {

      busy = false;

      if (button) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        button.textContent = originalText;
      }
    }
  };

  document.addEventListener('submit', (event) => {

    const form = event.target.closest('form[action*="/cart/add"]');

    if (!form) return;

    event.preventDefault();

    addForm(form);

  });

  cartRoot.addEventListener('click', (event) => {

    if (event.target.closest('[data-tymzo-cart-close]')) {
      closeCart();
      return;
    }

    const lineElement = event.target.closest('[data-tymzo-cart-line]');

    if (!lineElement) return;

    const line = Number(lineElement.dataset.line);

    const quantityInput =
      lineElement.querySelector('[data-tymzo-cart-quantity]');

    const currentQuantity = Number(quantityInput?.value || 1);
        if (event.target.closest('[data-tymzo-cart-minus]')) {
      changeLine(line, Math.max(0, currentQuantity - 1));
      return;
    }

    if (event.target.closest('[data-tymzo-cart-plus]')) {
      changeLine(line, currentQuantity + 1);
      return;
    }

    if (event.target.closest('[data-tymzo-cart-remove]')) {
      changeLine(line, 0);
    }
  });

  cartRoot.addEventListener('change', (event) => {
    const input = event.target.closest('[data-tymzo-cart-quantity]');
    if (!input) return;

    const lineElement = input.closest('[data-tymzo-cart-line]');
    if (!lineElement) return;

    const line = Number(lineElement.dataset.line);
    const quantity = Math.max(0, Number(input.value || 0));

    changeLine(line, quantity);
  });

  document.addEventListener('click', async (event) => {
    const trigger = event.target.closest(
      '[data-tymzo-cart-open], [aria-controls="TymzoAjaxCart"], [aria-controls="cart-drawer"], a[href="/cart"], a[href*="/cart"]'
    );

    if (!trigger || trigger.closest('[data-tymzo-cart]')) return;

    event.preventDefault();

    try {
      await refreshAndOpenCart();
    } catch (error) {
      console.error('[Tymzo Cart Open]', error);
      window.location.href = '/cart';
    }
  });

  document.addEventListener('cart:refresh', async () => {
    try {
      await refreshAndOpenCart();
    } catch (error) {
      console.error('[Tymzo Cart Refresh]', error);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (
      event.key === 'Escape' &&
      cartRoot.classList.contains('is-open')
    ) {
      closeCart();
    }
  });

  renderProgress({
    item_count: Number(
      cartRoot.querySelectorAll('[data-tymzo-cart-line]').length
    ),
    total_price:
      Number((total?.textContent || '').replace(/[^\d]/g, '')) || 0
  });
})();